/**
 * Sales Update Data Caching Utility
 *
 * Provides intelligent caching for sales update data with automatic expiration
 * and daily refresh logic for optimal user experience.
 */

// Cache configuration constants
const CACHE_KEY = "salesUpdateData";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const DAILY_REFRESH_THRESHOLD = 6 * 60 * 60 * 1000; // 6 hours - for daily refresh logic

/**
 * Generates a unique cache key based on filter parameters
 * @param {Object} filters - The filter object to generate key from
 * @returns {string} - Unique cache key
 */
const generateCacheKey = (filters) => {
  return `${CACHE_KEY}_${JSON.stringify(filters)}`;
};

/**
 * Intelligent cache decision maker that determines whether to fetch fresh data
 * and whether to use existing cached data for immediate display
 *
 * @param {Object} filters - Current filter parameters
 * @returns {Object} - Decision object with shouldFetch, useCache, and optional cachedData
 */
export const shouldFetchFreshData = (filters) => {
  try {
    const cacheKey = generateCacheKey(filters);
    const cachedItem = localStorage.getItem(cacheKey);

    if (!cachedItem) {
      console.log("No cache found, will fetch fresh data");
      return { shouldFetch: true, useCache: false };
    }

    const { data, timestamp } = JSON.parse(cachedItem);
    const now = Date.now();
    const cacheAge = now - timestamp;

    // If cache is expired (older than 24 hours), remove it and fetch fresh
    if (cacheAge >= CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      console.log("Cache expired, will fetch fresh data");
      return { shouldFetch: true, useCache: false };
    }

    // Check if it's a different day (simple daily refresh logic)
    const cachedDate = new Date(timestamp);
    const currentDate = new Date(now);
    const isDifferentDay = cachedDate.toDateString() !== currentDate.toDateString();

    // If it's a different day and cache is older than 6 hours, fetch fresh data
    // but still use cached data for immediate display
    if (isDifferentDay && cacheAge > DAILY_REFRESH_THRESHOLD) {
      console.log("Different day detected, will fetch fresh data but use cache initially");
      return { shouldFetch: true, useCache: true, cachedData: data };
    }

    // Cache is valid and recent, use it
    console.log("Using valid cached data");
    return { shouldFetch: false, useCache: true, cachedData: data };
  } catch (error) {
    console.error("Error checking cache:", error);
    return { shouldFetch: true, useCache: false };
  }
};

/**
 * Saves data to cache with current timestamp
 *
 * @param {Object} filters - Filter parameters used as cache key
 * @param {Object} data - Data to be cached
 */
export const setCachedData = (filters, data) => {
  try {
    const cacheKey = generateCacheKey(filters);
    const cacheItem = {
      data,
      timestamp: Date.now(),
    };

    localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    console.log("Sales update data cached successfully");
  } catch (error) {
    console.error("Error saving to cache:", error);
    // If localStorage is full, try to clear old entries
    clearExpiredCache();
  }
};

/**
 * Removes expired cache entries to free up storage space
 * Called automatically when localStorage is full or manually for cleanup
 */
export const clearExpiredCache = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        const cachedItem = localStorage.getItem(key);
        if (cachedItem) {
          try {
            const { timestamp } = JSON.parse(cachedItem);
            if (now - timestamp >= CACHE_DURATION) {
              keysToRemove.push(key);
            }
          } catch (parseError) {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`Cleared ${keysToRemove.length} expired cache entries`);
    }
  } catch (error) {
    console.error("Error clearing expired cache:", error);
  }
};

/**
 * Gets cache statistics for debugging/monitoring purposes
 *
 * @returns {Object} - Cache statistics including count, total size, oldest entry
 */
export const getCacheStats = () => {
  try {
    const stats = {
      totalEntries: 0,
      totalSizeBytes: 0,
      oldestEntry: null,
      newestEntry: null,
    };

    const now = Date.now();
    let oldestTimestamp = now;
    let newestTimestamp = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        const cachedItem = localStorage.getItem(key);
        if (cachedItem) {
          try {
            const { timestamp } = JSON.parse(cachedItem);
            stats.totalEntries++;
            stats.totalSizeBytes += new Blob([cachedItem]).size;

            if (timestamp < oldestTimestamp) {
              oldestTimestamp = timestamp;
              stats.oldestEntry = new Date(timestamp);
            }
            if (timestamp > newestTimestamp) {
              newestTimestamp = timestamp;
              stats.newestEntry = new Date(timestamp);
            }
          } catch (parseError) {
            // Invalid entry, still count it
            stats.totalEntries++;
            stats.totalSizeBytes += new Blob([cachedItem]).size;
          }
        }
      }
    }

    return stats;
  } catch (error) {
    console.error("Error getting cache stats:", error);
    return { totalEntries: 0, totalSizeBytes: 0, oldestEntry: null, newestEntry: null };
  }
};

/**
 * Clears all sales update cache entries (for development/debugging)
 * USE WITH CAUTION: This will remove all cached data
 * This is not being referenced anywhere at the moment (it should not!).
 */
export const clearAllCache = () => {
  try {
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log(`Cleared all ${keysToRemove.length} sales update cache entries`);
    return keysToRemove.length;
  } catch (error) {
    console.error("Error clearing all cache:", error);
    return 0;
  }
};
